import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { useEmojiLabel } from '../features/emoji/useEmojiLabel';
import { EmojiPicker } from '../features/profiles/EmojiPicker';
import { removeProfile, updateProfile } from '../features/profiles/profileMutations';
import {
  addRoutine,
  getProfileRoutines,
  nextLibrarySortIndex,
  reorderProfileRoutines,
} from '../features/profiles/routineMutations';
import { ThemePicker } from '../features/profiles/ThemePicker';
import type { Routine } from '../lib/types';
import { uid } from '../lib/uid';
import { useSyncedStore } from '../store/synced';
import { resolveTheme } from '../themes/resolveTheme';

// Profile editor per plan/03 §Profile editor. v1 scope: name, avatar,
// routines list (linked to the routine editor — M4 #3), delete-profile
// (refuses on the last). Theme picker entry button is a stub for M3.
//
// Edits flow through useSyncedStore.setTree, which is reasonable while the
// tree is still small (<50 KB per plan/02 acceptance #5).
export function ProfileEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tree = useSyncedStore((s) => s.tree);
  const setTree = useSyncedStore((s) => s.setTree);
  const profile = tree?.profiles.find((p) => p.id === id);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeRoutineId, setActiveRoutineId] = useState<string | null>(null);

  const avatarLabel = useEmojiLabel(profile?.avatarEmoji ?? '');

  // Same activation constraints as RoutineEditorPage: pointer distance 5px
  // and touch long-press 150ms keep tap-to-navigate on the routine row from
  // being mistaken for a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!tree || !profile) return <Navigate to="/settings" replace />;

  const isLast = tree.profiles.length <= 1;
  // v3: the profile's routines are resolved from the library in the order
  // stored on `profile.routineIds`.
  const routines = getProfileRoutines(tree, profile.id);

  const onName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTree(updateProfile(tree, profile.id, { name: e.target.value }));
  };

  const onPickEmoji = (emoji: string) => {
    setTree(updateProfile(tree, profile.id, { avatarEmoji: emoji }));
    setPickerOpen(false);
  };

  const onDelete = () => {
    const next = removeProfile(tree, profile.id);
    if (!next) return;
    setTree(next);
    navigate('/settings', { replace: true });
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActiveRoutineId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = routines.map((r) => r.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    setTree(reorderProfileRoutines(tree, profile.id, arrayMove(ids, oldIndex, newIndex)));
  };

  const activeRoutine = activeRoutineId
    ? (routines.find((r) => r.id === activeRoutineId) ?? null)
    : null;

  const onAddRoutine = () => {
    const ts = new Date().toISOString();
    const newRoutine: Routine = {
      id: uid(),
      name: '',
      emoji: '✨',
      sortIndex: nextLibrarySortIndex(tree),
      tasks: [],
      createdAt: ts,
      updatedAt: ts,
    };
    setTree(addRoutine(tree, profile.id, newRoutine, ts));
    navigate(`/settings/profiles/${profile.id}/list/${newRoutine.id}`);
  };

  return (
    <main className="settings">
      <Link to="/settings" className="back-button" aria-label="Takaisin asetuksiin">
        <span aria-hidden="true">←</span>
      </Link>
      <h1 className="settings-title">Profiili</h1>

      <section className="settings-section">
        <h2 className="settings-section-title">Avatar ja nimi</h2>
        <div className="profile-edit-row">
          <button
            type="button"
            className="profile-edit-avatar"
            onClick={() => setPickerOpen(true)}
            aria-label={`Vaihda avatar (nyt: ${avatarLabel})`}
          >
            <span aria-hidden="true">{profile.avatarEmoji}</span>
          </button>
          <input
            type="text"
            className="profile-edit-name"
            value={profile.name}
            onChange={onName}
            aria-label="Profiilin nimi"
            placeholder="Lapsen nimi"
          />
        </div>
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">Teema</h2>
        <p className="settings-hint">Nykyinen teema: {resolveTheme(profile).name}</p>
        <button type="button" className="settings-button" onClick={() => setThemePickerOpen(true)}>
          Valitse teema
        </button>
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">Rutiinit</h2>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e) => setActiveRoutineId(String(e.active.id))}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveRoutineId(null)}
        >
          <SortableContext items={routines.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <ul className="settings-profile-list settings-profile-list--sortable">
              {routines.map((r: Routine) => (
                <SortableRoutineRow key={r.id} routine={r} profileId={profile.id} />
              ))}
            </ul>
          </SortableContext>
          <DragOverlay>
            {activeRoutine && (
              <ul className="settings-profile-list settings-profile-list--sortable">
                <li className="settings-profile-row-item" data-overlay="true">
                  <RoutineRowContent routine={activeRoutine} profileId={profile.id} isOverlay />
                </li>
              </ul>
            )}
          </DragOverlay>
        </DndContext>
        <button type="button" className="settings-button" onClick={onAddRoutine}>
          + Lisää rutiini
        </button>
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">Vaarallinen alue</h2>
        <p className="settings-hint">
          {isLast
            ? 'Tämä on viimeinen profiili — sitä ei voi poistaa.'
            : 'Profiilin poistaminen pyyhkii sen rutiinit ja edistymisen.'}
        </p>
        <button
          type="button"
          className="settings-button settings-button-danger"
          onClick={() => setConfirmDelete(true)}
          disabled={isLast}
        >
          Poista profiili
        </button>
      </section>

      {pickerOpen && <EmojiPicker onPick={onPickEmoji} onCancel={() => setPickerOpen(false)} />}
      {themePickerOpen && (
        <ThemePicker
          currentKey={profile.theme.default}
          {...(profile.theme.custom ? { currentRecipe: profile.theme.custom } : {})}
          onApply={(next) => {
            let updatedTheme;
            if (next.kind === 'preset') {
              const { custom, ...rest } = profile.theme;
              updatedTheme = custom === undefined ? profile.theme : rest;
              updatedTheme = { ...updatedTheme, default: next.key };
            } else if (next.kind === 'customLibrary') {
              const { custom, ...rest } = profile.theme;
              updatedTheme = custom === undefined ? profile.theme : rest;
              updatedTheme = { ...updatedTheme, default: next.id };
            } else {
              updatedTheme = { ...profile.theme, default: 'custom', custom: next.recipe };
            }
            setTree(updateProfile(tree, profile.id, { theme: updatedTheme }));
            setThemePickerOpen(false);
          }}
          onCancel={() => setThemePickerOpen(false)}
        />
      )}
      {confirmDelete && (
        <ConfirmDelete
          name={profile.name}
          onConfirm={onDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </main>
  );
}

function RoutineRowContent({
  routine,
  profileId,
  isOverlay = false,
  handleAttributes,
  handleListeners,
}: Readonly<{
  routine: Routine;
  profileId: string;
  isOverlay?: boolean;
  handleAttributes?: React.HTMLAttributes<HTMLButtonElement>;
  handleListeners?: React.HTMLAttributes<HTMLButtonElement>;
}>) {
  return (
    <>
      <button
        type="button"
        className="settings-profile-row-handle"
        aria-label={`Siirrä rutiini: ${routine.name || 'rutiini'}`}
        // Drag overlay is a pure visual clone — the source li holds the
        // real sortable listeners, so the overlay shouldn't try to start a
        // second drag.
        {...(isOverlay ? {} : (handleAttributes ?? {}))}
        {...(isOverlay ? {} : (handleListeners ?? {}))}
      >
        <span aria-hidden="true">⠿</span>
      </button>
      <Link
        to={`/settings/profiles/${profileId}/list/${routine.id}`}
        className="settings-profile-row"
      >
        <span className="settings-profile-avatar" aria-hidden="true">
          {routine.emoji}
        </span>
        <span className="settings-profile-name">{routine.name}</span>
        <span className="settings-profile-edit" aria-hidden="true">
          ✎
        </span>
      </Link>
    </>
  );
}

function SortableRoutineRow({
  routine,
  profileId,
}: Readonly<{ routine: Routine; profileId: string }>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: routine.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="settings-profile-row-item"
      data-dragging={isDragging || undefined}
    >
      <RoutineRowContent
        routine={routine}
        profileId={profileId}
        handleAttributes={attributes as React.HTMLAttributes<HTMLButtonElement>}
        handleListeners={listeners as React.HTMLAttributes<HTMLButtonElement>}
      />
    </li>
  );
}

function ConfirmDelete({
  name,
  onConfirm,
  onCancel,
}: {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="confirm-modal" role="dialog" aria-label="Vahvista poisto">
      <button type="button" className="confirm-backdrop" aria-label="Sulje" onClick={onCancel} />
      <div className="confirm-box">
        <h2>Poista profiili?</h2>
        <p>{name} ja kaikki sen rutiinit poistetaan.</p>
        <div className="confirm-actions">
          <button type="button" className="settings-button" onClick={onCancel}>
            Peruuta
          </button>
          <button
            type="button"
            className="settings-button settings-button-danger"
            onClick={onConfirm}
          >
            Poista
          </button>
        </div>
      </div>
    </div>
  );
}
